import clsx from 'clsx';
import React from 'react';
import { InView } from 'react-intersection-observer';

import Byline from '@components/Byline';
import ArticleHeader from '@components/FeedItems/ArticleHeader';
import ArticleImage from '@components/FeedItems/ArticleImage';
import ArticleWrapper from '@components/FeedItems/ArticleWrapper';
import Highlight from '@components/FeedItems/Highlight';
import { useEventTracking, useWindowSize } from '@hooks';
import { Author } from '@interfaces/authors';
import { ImpressionElement, impression } from '@tracking';
import { WIDTH_SM } from '@utils/constants';

interface StoryItemMidArticleProps {
  authors: Author[];
  highlight: string;
  id: number;
  index: number;
  isPro: boolean;
  picture?: string;
  publishedAt: string;
  slug: string;
  title: string;
  newsletterSlug?: string;
  posterPicture?: string;
}

const StoryItemMidArticle = ({
  authors,
  highlight,
  id,
  index,
  isPro,
  picture,
  publishedAt,
  slug,
  title,
  newsletterSlug,
  posterPicture,
}: StoryItemMidArticleProps) => {
  const { width: windowWidth } = useWindowSize();
  const { Track, trackEvent } = useEventTracking({
    contentIndex: index,
  });

  const handleInViewOnChange = (inView) => {
    inView &&
      windowWidth < WIDTH_SM &&
      trackEvent(impression({ impressionElement: ImpressionElement.RelatedStory }));
  };

  return (
    <ArticleWrapper
      articleId={id}
      articleAuthors={authors}
      className="w-full max-w-[54.6vw] flex-col sm:max-w-none md:max-w-[213px] lg:max-w-[257px]"
    >
      <Track>
        <InView
          className="sm:flex sm:flex-row sm:gap-16 md:block md:flex-col md:gap-0"
          threshold={0.5}
          onChange={handleInViewOnChange}
          triggerOnce
        >
          <ArticleImage
            className="aspect-[16/9] min-w-full leading-[0] max-sm:!h-[calc(54.6vw*9/16)] sm:min-w-0 sm:max-w-[213px] lg:max-w-full"
            id={id}
            isPro={isPro}
            picture={picture}
            sizes="(min-width: 992px) 250px, (min-width: 480px) 600px, 400px"
            slug={slug}
            target="_blank"
            title={title}
            newsletterSlug={newsletterSlug}
            posterPicture={posterPicture}
          />
          <div className="mt-6 sm:mt-0 md:mt-[14px] lg:mt-6">
            {highlight && (
              <Highlight
                highlight={highlight}
                className="!mb-0 mt-10 font-suisse-intl-mono !text-caption-sm !no-underline sm:mt-0 md:mt-10"
              />
            )}
            <ArticleHeader
              className={clsx('text-headline-xxs *:!no-underline', {
                'mt-2': highlight,
                'mt-10': !highlight,
              })}
              slug={slug}
              target="_blank"
              title={title}
              newsletterSlug={newsletterSlug}
            />
            <Byline
              authors={authors}
              authorsClassName="author-name-related-story"
              className="*:*:*:!no-underline"
              publishedAt={publishedAt}
              newsletterSlug={newsletterSlug}
              timeFromNow
            />
          </div>
        </InView>
      </Track>
    </ArticleWrapper>
  );
};

export default StoryItemMidArticle;
